import React from 'react';
import { Box, Typography, Button } from '@mui/material';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to backend or console
    if (process.env.NODE_ENV === 'production') {
      fetch('/api/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.toString(),
          errorInfo,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      });
    } else {
      console.error('ErrorBoundary caught error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      let fallback = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            textAlign: 'center',
            p: 4,
          }}
        >
          <Typography variant="h3" color="error" gutterBottom>
            Oops!
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Something went wrong. We're working to fix it.
            <br />
            Please try refreshing the page or come back later.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </Box>
      );
      // Standardized fallback UI
      if (error && error.message) {
        if (/network/i.test(error.message)) {
          fallback = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                p: 4,
              }}
            >
              <Typography variant="h3" color="error" gutterBottom>
                Network Error
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Network error. Please check your connection or try again later.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Box>
          );
        } else if (/permission|unauthorized|forbidden/i.test(error.message)) {
          fallback = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                p: 4,
              }}
            >
              <Typography variant="h3" color="error" gutterBottom>
                Permission Error
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Permission error. You do not have access to this resource.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Box>
          );
        } else {
          fallback = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                p: 4,
              }}
            >
              <Typography variant="h3" color="error" gutterBottom>
                Unexpected Error
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Unexpected error: {error.message}
              </Typography>
              <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Box>
          );
        }
      }
      return fallback;
    }
    return this.props.children;
  }
}
